import { useEffect, useMemo, useState } from "react";
import { api, formatError, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import PlayerAvatar from "@/components/app/PlayerAvatar";
import PlayerEditDialog from "@/components/app/PlayerEditDialog";
import { RotateCcw, Download, FileText, Pencil, MoonStar } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

export default function Archive() {
  const [players, setPlayers] = useState([]);
  const [stats, setStats] = useState({});
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const [p, s] = await Promise.all([
      api.get("/players", { params: { status_filter: "graduated,inactive" } }),
      api.get("/game-stats"),
    ]);
    setPlayers(p.data);
    const m = {};
    for (const st of s.data) (m[st.player_id] = m[st.player_id] || []).push(st);
    setStats(m);
  };
  useEffect(() => { load(); }, []);

  const { gradGrouped, inactives } = useMemo(() => {
    const g = {};
    const inactives = [];
    for (const p of players) {
      if (p.status === "inactive") {
        inactives.push(p);
      } else {
        const y = p.graduation_year || "Unknown";
        (g[y] = g[y] || []).push(p);
      }
    }
    return { gradGrouped: g, inactives };
  }, [players]);

  const restore = async (p) => {
    try {
      await api.post(`/players/${p.id}/restore`);
      toast.success(`Restored ${p.player_name}`);
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const downloadServerFile = async (path, filename) => {
    try {
      const t = localStorage.getItem("nphs_token");
      const res = await fetch(`${API_BASE}${path}`, { credentials: "include", headers: t ? { Authorization: `Bearer ${t}` } : {} });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const exportAlumni = () => downloadServerFile("/export/alumni", "NPHS_Alumni.xlsx");
  const exportSeasonRecap = () => downloadServerFile("/export/season-recap", "NPHS_Canucks_Season_Recap.pdf");

  const careerStats = (p) => {
    const ss = stats[p.id] || [];
    const gp = ss.length;
    const pts = ss.reduce((a, s) => a + 2 * (s.fg2_made || 0) + 3 * (s.fg3_made || 0) + (s.ft_made || 0), 0);
    return { gp, pts, ppg: gp ? (pts / gp).toFixed(1) : "0.0" };
  };

  const totalArchived = players.length;

  return (
    <div className="p-6 md:p-10">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <p className="eyebrow">Alumni & Inactive Players</p>
          <h1 className="heading-1">Player <span className="text-burgundy">Archive</span></h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={exportSeasonRecap} variant="outline" className="border-ink-700 hover:bg-ink-800" data-testid="archive-recap-pdf-btn">
            <FileText className="w-4 h-4 mr-2" /> Season Recap PDF
          </Button>
          <Button onClick={exportAlumni} className="bg-burgundy hover:bg-burgundy-hover" data-testid="archive-export-btn">
            <Download className="w-4 h-4 mr-2" /> Export Alumni
          </Button>
        </div>
      </div>

      {totalArchived === 0 ? (
        <div className="bg-ink-800 rounded-lg border border-white/5 p-10 text-center text-ink-400">
          No archived players yet. Use the Archive button on the Roster page (or set status to Graduated/Inactive in the Edit dialog) to move a player here.
        </div>
      ) : (
        <>
          {Object.entries(gradGrouped).sort(([a], [b]) => String(b).localeCompare(String(a))).map(([year, ps]) => (
            <section key={year} className="mb-8">
              <h2 className="heading-3 mb-3 text-ink-300">Class of <span className="text-white">{year}</span></h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {ps.map((p) => (
                  <PlayerCard
                    key={p.id}
                    p={p}
                    career={careerStats(p)}
                    onRestore={() => restore(p)}
                    onEdit={() => setEditing(p)}
                    badge={{ label: "Graduated", className: "bg-amber-500/15 text-amber-400" }}
                  />
                ))}
              </div>
            </section>
          ))}

          {inactives.length > 0 && (
            <section className="mb-8">
              <h2 className="heading-3 mb-3 text-ink-300 flex items-center gap-2">
                <MoonStar className="w-5 h-5 text-ink-400" /> Inactive
                <span className="text-xs text-ink-400 normal-case tracking-normal font-normal">(not on roster, not yet graduated)</span>
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {inactives.map((p) => (
                  <PlayerCard
                    key={p.id}
                    p={p}
                    career={careerStats(p)}
                    onRestore={() => restore(p)}
                    onEdit={() => setEditing(p)}
                    badge={{ label: "Inactive", className: "bg-ink-700 text-ink-200" }}
                  />
                ))}
              </div>
            </section>
          )}
        </>
      )}

      <PlayerEditDialog
        open={!!editing}
        player={editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
      />
    </div>
  );
}

function PlayerCard({ p, career, onRestore, onEdit, badge }) {
  return (
    <div className="bg-ink-800 rounded-lg border border-white/5 p-4" data-testid={`archive-card-${p.slug}`}>
      <div className="flex items-center gap-3">
        <PlayerAvatar player={p} size="lg" />
        <div className="flex-1 min-w-0">
          <Link to={`/player/${p.slug}`} className="font-display text-lg font-bold uppercase hover:text-burgundy block truncate">{p.player_name}</Link>
          <p className="text-ink-400 text-xs">#{p.jersey_number || "—"} · {p.position || "—"}</p>
          <span className={`inline-block mt-1 px-2 py-0.5 text-[10px] uppercase tracking-wider rounded ${badge.className}`}>{badge.label}</span>
        </div>
      </div>
      <div className="flex justify-between text-xs uppercase tracking-wider text-ink-400 mt-3">
        <div><span>GP </span><span className="font-display text-white text-base">{career.gp}</span></div>
        <div><span>PTS </span><span className="font-display text-white text-base">{career.pts}</span></div>
        <div><span>PPG </span><span className="font-display text-white text-base">{career.ppg}</span></div>
      </div>
      <div className="flex gap-2 mt-3">
        <Button onClick={onEdit} size="sm" variant="outline" className="flex-1 border-ink-700 hover:bg-ink-700" data-testid={`archive-edit-${p.slug}`}>
          <Pencil className="w-3 h-3 mr-1" /> Edit
        </Button>
        <Button onClick={onRestore} size="sm" variant="outline" className="flex-1 border-ink-700 hover:bg-ink-700" data-testid={`archive-restore-${p.slug}`}>
          <RotateCcw className="w-3 h-3 mr-1" /> Restore
        </Button>
      </div>
    </div>
  );
}
